import {
  Button,
  ComponentType,
  FormFieldMethods,
  Grid,
  IOption,
  UseFormReturn,
  convertDateToStringIgnoringTimezone,
} from '@gcl/react-core';
import * as React from 'react';
import './PermitToLoadRemoveDetailPage.scss';
import { DetailPageTopBar, IDetailPageTopBarItem } from '@app/DetailPageTopBar';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { useProjectId } from '@config/config.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import { routes } from '@routes/routes.models';
import { useNavigate } from '@routes/routes.hooks';
import {
  useApproveLoadingRemovalCertRemovalCertIceAsync,
  useApproveLoadingRemovalCertRemovalCertTwcAsync,
  useCreateLoadingRemovalCertAsync,
  useCreateLoadingRemovalCertRemovalCertAsync,
  useCreateLoadingRemovalCertRevisionAsync,
  useDeleteLoadingRemovalCertAsync,
  useIssueLoadingRemovalCertRemovalCertSrpAsync,
  useRequestLoadingRemovalCertIceCertAsync,
  useUpdateLoadingRemovalCertAsync,
} from '@permitToLoadRemove/permitToLoadRemove.queries';
import { useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { EPermitToLoadStatus } from '@permitToLoadRemove/permitToLoadRemove.models';
import { IProjectDrawingRegisterSearch } from '@drawing/projectDrawingRegister.models';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { DownloadButton } from '@file/DownloadButton';
import {
  downloadLoadingRemovalCertPdfAsync,
  downloadLoadingRemovalCertIceConstructionCertPdfAsync,
  downloadLoadingRemovalCertIceRemovalCertPdfAsync,
} from '@permitToLoadRemove/permitToLoadRemove.services';
import { IProjectMasterScheduleTwcOption } from '@masterSchedule/masterSchedule.models';
import { useDeferLoading } from '@common/loading.hooks';
import { PermitToLoadRemoveDetailPermit } from './PermitToLoadRemoveDetailPermit';
import {
  ICreateProjectLoadingCertRequest,
  IProjectLoadingRemovalCert,
  IProjectLoadingRemovalCertT3,
  IUpdateProjectLoadingRemovalCertRequest,
} from './permitToLoadRemoveDetailPage.models';
import { PermitToLoadRemoveDetailRelatedRemovalCert } from './PermitToLoadRemoveDetailRelatedRemovalCert';
import { PermitToLoadRemoveDetailTabForm } from './PermitToLoadRemoveDetailTabForm';
import {
  ECreateT4FormKeys,
  ELoadingRemovalCertConfirmationActionType,
  EPermitToLoadRemoveDetailPermitType,
} from './permitToLoadRemoveDetailPage.constants';
import { usePermitToLoadRemoveConfirmationModal } from './permitToLoadRemoveConfirmationModal.hooks';
import { useT4PermitsVisibility, useT4TopBtnsVisibility } from './permitToLoadRemovalDetailPage.hooks';
import { usePermitToLoadRemovalSelectTwcModal } from './permitToLoadRemovalSelectTwcModal.hooks';
import { PermitToLoadRemovalDetailPermitIce } from './PermitToLoadRemoveDetailPermitIce';

interface IPermitToLoadRemoveDetailTabDetailProps {
  isReadOnly: boolean;
  loadingRemovalCertDetail?: IProjectLoadingRemovalCert;
  loadingRemovalCertId?: number;
  masterScheduleId?: number;
  masterSchedule?: IMasterScheduleDetail;
  designModifications: IProjectLoadingRemovalCertT3[];
  mode: DetailPageAccessMode;
  isSuperseded: boolean;
  formFieldMethods: FormFieldMethods;
  useFormMethods: UseFormReturn;
}
export const PermitToLoadRemoveDetailTabDetail: React.FunctionComponent<IPermitToLoadRemoveDetailTabDetailProps> = ({
  isReadOnly,
  loadingRemovalCertDetail,
  loadingRemovalCertId,
  masterScheduleId,
  masterSchedule,
  designModifications,
  mode,
  isSuperseded,
  formFieldMethods,
  useFormMethods,
}) => {
  const projectId = useProjectId();
  const { navigate } = useNavigate();
  const queryClient = useQueryClient();
  const t4TopBtnsVisibility = useT4TopBtnsVisibility(isSuperseded, mode, loadingRemovalCertDetail, masterSchedule);
  const t4PermitsVisibility = useT4PermitsVisibility(isSuperseded, loadingRemovalCertDetail, masterSchedule);

  const isFinalRemovalCert = formFieldMethods.watch(ECreateT4FormKeys.IS_FINAL_REMOVAL_CERT);

  const [confirmationActionType, setConfirmationActionType] =
    React.useState<ELoadingRemovalCertConfirmationActionType>();

  const { renderModal: renderConfirmationPortal, openPortal: openConfirmationPortal } =
    usePermitToLoadRemoveConfirmationModal({
      confirmationActionType,
      onConfirm: () => {
        switch (confirmationActionType) {
          case ELoadingRemovalCertConfirmationActionType.delete:
          case ELoadingRemovalCertConfirmationActionType.removeRemovalCert:
            if (loadingRemovalCertId) mutateDelete(loadingRemovalCertId);
            break;
          case ELoadingRemovalCertConfirmationActionType.createRevision:
            if (loadingRemovalCertId) mutateCreateRevision(loadingRemovalCertId);
            break;
          case ELoadingRemovalCertConfirmationActionType.createRemovalCert:
            if (loadingRemovalCertId) mutateCreateRemovalCert(loadingRemovalCertId);
            break;
          case ELoadingRemovalCertConfirmationActionType.srpIssue:
            useFormMethods.handleSubmit(handleSubmit('srpIssued'))();
            break;
          case ELoadingRemovalCertConfirmationActionType.twcApprove:
            useFormMethods.handleSubmit(handleSubmit('twcApproved'))();
            break;
          case ELoadingRemovalCertConfirmationActionType.iceApprove:
            useFormMethods.handleSubmit(handleSubmit('iceApproved'))();
            break;
          case ELoadingRemovalCertConfirmationActionType.requestIceCert:
            if (loadingRemovalCertId)
              mutateRequestIceCert({
                isWithDrawal: false,
                id: loadingRemovalCertId,
              });
            break;
          case ELoadingRemovalCertConfirmationActionType.withdrawIceCert:
            if (loadingRemovalCertId)
              mutateRequestIceCert({
                isWithDrawal: true,
                id: loadingRemovalCertId,
              });
            break;
          default:
            break;
        }
      },
    });

  const mutationCreate = useCreateLoadingRemovalCertAsync();
  const {
    mutate: mutateCreate,
    isLoading: isLoadingCreate,
    isError: isErrorCreate,
    isSuccess: isSuccessCreate,
    data: dataCreate,
    error: errorCreate,
    reset: resetCreate,
  } = mutationCreate;

  const {
    mutate: mutateUpdate,
    isLoading: isLoadingUpdate,
    isError: isErrorUpdate,
    isSuccess: isSuccessUpdate,
    data: dataUpdate,
    reset: resetUpdate,
    error: errorUpdate,
  } = useUpdateLoadingRemovalCertAsync();

  const {
    mutate: mutateDelete,
    isLoading: isLoadingDelete,
    isError: isErrorDelete,
    isSuccess: isSuccessDelete,
    data: dataDelete,
    error: errorDelete,
    reset: resetDelete,
  } = useDeleteLoadingRemovalCertAsync();

  const {
    mutate: mutateCreateRevision,
    isLoading: isLoadingCreateRevision,
    isError: isErrorCreateRevision,
    isSuccess: isSuccessCreateRevision,
    data: dataCreateRevision,
    error: errorCreateRevision,
    reset: resetCreateRevision,
  } = useCreateLoadingRemovalCertRevisionAsync();
  const {
    mutate: mutateCreateRemovalCert,
    isLoading: isLoadingCreateRemovalCert,
    isError: isErrorCreateRemovalCert,
    isSuccess: isSuccessCreateRemovalCert,
    data: dataCreateRemovalCert,
    error: errorCreateRemovalCert,
    reset: resetCreateRemovalCert,
  } = useCreateLoadingRemovalCertRemovalCertAsync();
  const {
    mutate: mutateRequestIceCert,
    isLoading: isLoadingRequestIceCert,
    isError: isErrorRequestIceCert,
    isSuccess: isSuccessRequestIceCert,
    data: dataRequestIceCert,
    error: errorRequestIceCert,
    reset: resetRequestIceCert,
  } = useRequestLoadingRemovalCertIceCertAsync();

  const mutationIssueSrp = useIssueLoadingRemovalCertRemovalCertSrpAsync();
  const {
    mutate: mutateIssueSrp,
    isLoading: isLoadingIssueSrp,
    isError: isErrorIssueSrp,
    isSuccess: isSuccessIssueSrp,
    data: dataIssueSrp,
    error: errorIssueSrp,
    reset: resetIssueSrp,
  } = mutationIssueSrp;

  const {
    mutate: mutateApproveIce,
    isLoading: isLoadingApproveIce,
    isError: isErrorApproveIce,
    isSuccess: isSuccessApproveIce,
    data: dataApproveIce,
    error: errorApproveIce,
    reset: resetApproveIce,
  } = useApproveLoadingRemovalCertRemovalCertIceAsync();

  const mutationApproveTwc = useApproveLoadingRemovalCertRemovalCertTwcAsync();
  const {
    mutate: mutateApproveTwc,
    isLoading: isLoadingApproveTwc,
    isError: isErrorApproveTwc,
    isSuccess: isSuccessApproveTwc,
    data: dataApproveTwc,
    error: errorApproveTwc,
    reset: resetApproveTwc,
  } = mutationApproveTwc;

  const { loading: isAnyActionLoading, setLoading: setIsAnyActionLoading } = useDeferLoading(
    isLoadingCreate ||
      isLoadingUpdate ||
      isLoadingDelete ||
      isLoadingCreateRevision ||
      isLoadingCreateRemovalCert ||
      isLoadingRequestIceCert ||
      isLoadingIssueSrp ||
      isLoadingApproveIce ||
      isLoadingApproveTwc,
  );

  const handleSubmit = (type: 'create' | 'update' | 'srpIssued' | 'twcApproved' | 'iceApproved') => (formData: any) => {
    const { twcCommentRemoval, twcCommentLoad, iceCommentRemoval, iceCommentLoad, selectedTwcs, ...data } = formData;
    const convertedDrawings = (
      (data[ECreateT4FormKeys.RELEASED_FOR_CONSTRUCTION_DRAWINGS] ?? []) as IOption<IProjectDrawingRegisterSearch>[]
    ).map((item) => {
      const existingDrawing = loadingRemovalCertDetail?.project_LoadingRemovalCertDrawing?.find(
        (d) => d.projectDrawingRegisterId === item.data?.id,
      );
      return {
        ...existingDrawing,
        loadingRemovalCertId,
        projectDrawingRegisterId: item.data?.id,
        projectDrawingRegisterHistoryId: item.data?.latestRfcHistory?.id,
        status: existingDrawing?.status ?? 'A',
      };
    });

    const createRequest: ICreateProjectLoadingCertRequest = {
      ...data,
      [ECreateT4FormKeys.RELEASED_FOR_CONSTRUCTION_DRAWINGS]: convertedDrawings,
      projectId,
      projectMasterScheduleId: masterScheduleId,
      [ECreateT4FormKeys.TWC_COMMENT]: loadingRemovalCertDetail?.isRemovalCert ? twcCommentRemoval : twcCommentLoad,
      [ECreateT4FormKeys.ICE_COMMENT]: loadingRemovalCertDetail?.isRemovalCert ? iceCommentRemoval : iceCommentLoad,
      [ECreateT4FormKeys.SELECTED_TWCS]: ((selectedTwcs ?? []) as IOption<IProjectMasterScheduleTwcOption>[]).map(
        (option) => option.data,
      ),
      [ECreateT4FormKeys.PERMIT_VALID_UNTIL]: data[ECreateT4FormKeys.PERMIT_VALID_UNTIL]
        ? convertDateToStringIgnoringTimezone(new Date(data[ECreateT4FormKeys.PERMIT_VALID_UNTIL]))
        : undefined,
    };

    if (type === 'create') {
      mutateCreate({ createRequest, type: 'create' });
      return;
    }
    if (type === 'srpIssued' && !loadingRemovalCertId) {
      mutateCreate({ createRequest, type: 'createAndIssue' });
      return;
    }

    if (!loadingRemovalCertId || !loadingRemovalCertDetail?.id) return;

    const updateRequest: IUpdateProjectLoadingRemovalCertRequest = {
      ...loadingRemovalCertDetail,
      ...createRequest,
      id: loadingRemovalCertDetail.id ?? loadingRemovalCertId,
    };

    if (type === 'update') {
      mutateUpdate(updateRequest);
      return;
    }
    if (type === 'srpIssued') {
      mutateIssueSrp(updateRequest);
      return;
    }
    if (type === 'twcApproved') {
      mutateApproveTwc(updateRequest);
      return;
    }
    if (type === 'iceApproved') {
      mutateApproveIce(updateRequest);
    }
  };

  const onClickIssueSrp = (e: React.MouseEvent) => {
    setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.srpIssue);
    openConfirmationPortal(e);
  };

  const {
    openPortal: openSelectTwcModal,
    renderModal: renderSelectTwcModal,
    closePortal: closeSelectTwcModal,
  } = usePermitToLoadRemovalSelectTwcModal({
    masterSchedule,
    onClickIssueSrp,
    mainFormFieldMethods: formFieldMethods,
    isEnable: t4TopBtnsVisibility.isShowSelectTwcBtn,
  });

  const topBarLeftItems = React.useMemo(() => {
    const topBtns: IDetailPageTopBarItem[] = [];

    if (t4TopBtnsVisibility.isShowSaveBtn)
      topBtns.push({
        buttonIconName: 'FolderCheck',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Primary,
        isError: (isErrorCreate && confirmationActionType === undefined) || isErrorUpdate,
        isLoading: (isLoadingCreate && confirmationActionType === undefined) || isLoadingUpdate,
        isSuccess: (isSuccessCreate && confirmationActionType === undefined) || isSuccessUpdate,
        title: 'Save',
        onClick: () => {
          setConfirmationActionType(undefined);
          useFormMethods.handleSubmit(handleSubmit(mode === 'Create' ? 'create' : 'update'))();
        },
        disable: isAnyActionLoading,
      });
    if (t4TopBtnsVisibility.isShowIssueToTwcBtn)
      topBtns.push({
        buttonIconName: 'EnvelopeCheck',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError:
          isErrorIssueSrp ||
          (isErrorCreate && confirmationActionType === ELoadingRemovalCertConfirmationActionType.srpIssue),
        isLoading:
          isLoadingIssueSrp ||
          (isLoadingCreate && confirmationActionType === ELoadingRemovalCertConfirmationActionType.srpIssue),
        isSuccess:
          isSuccessIssueSrp ||
          (isSuccessCreate && confirmationActionType === ELoadingRemovalCertConfirmationActionType.srpIssue),
        title: 'Issue to TWC',
        onClick: onClickIssueSrp,
        disable: isAnyActionLoading,
      });

    if (t4TopBtnsVisibility.isShowApproveBtn) {
      const isApproveTwc =
        loadingRemovalCertDetail?.status === EPermitToLoadStatus.WAITING_FOR_TWC ||
        loadingRemovalCertDetail?.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL;
      const isApproveIce =
        loadingRemovalCertDetail?.status === EPermitToLoadStatus.WAITING_FOR_ICE ||
        loadingRemovalCertDetail?.status === EPermitToLoadStatus.WAITING_FOR_ICE_REMOVAL;

      topBtns.push({
        buttonIconName: 'Pen',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Primary,
        isError: isErrorApproveTwc || isErrorApproveIce,
        isLoading: isLoadingApproveTwc || isLoadingApproveIce,
        isSuccess: isSuccessApproveTwc || isSuccessApproveIce,
        title: isApproveTwc ? 'Approve as TWC' : 'Approve as ICE',
        onClick: (e: any) => {
          if (isApproveTwc) {
            setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.twcApprove);
            openConfirmationPortal(e);
          }
          if (isApproveIce) {
            setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.iceApprove);
            openConfirmationPortal(e);
          }
        },
        disable: isAnyActionLoading,
      });
    }
    if (t4TopBtnsVisibility.isShowCreateRevisionBtn)
      topBtns.push({
        buttonIconName: 'Stickies',
        title: 'Create Revision',
        isError: isErrorCreateRevision,
        isLoading: isLoadingCreateRevision,
        isSuccess: isSuccessCreateRevision,
        onClick: (e: any) => {
          setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.createRevision);
          openConfirmationPortal(e);
        },
        componentType: ComponentType.Info,
        type: EDetailPageTopBarItemType.loadingButton,
        disable: isAnyActionLoading,
      });

    if (t4TopBtnsVisibility.isShowCreateUnloadBtn && !isFinalRemovalCert)
      topBtns.push({
        buttonIconName: 'FileEarmarkPlus',
        title: 'Create Unload cert',
        isError: isErrorCreateRemovalCert,
        isLoading: isLoadingCreateRemovalCert,
        isSuccess: isSuccessCreateRemovalCert,
        onClick: (e: any) => {
          setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.createRemovalCert);
          openConfirmationPortal(e);
        },
        componentType: ComponentType.Info,
        type: EDetailPageTopBarItemType.loadingButton,
        disable: isAnyActionLoading,
      });

    if (t4TopBtnsVisibility.isShowRemoveRemovalCertBtn)
      topBtns.push({
        buttonIconName: 'FileExcel',
        title: 'Remove Unload cert',
        isError:
          isErrorDelete && confirmationActionType === ELoadingRemovalCertConfirmationActionType.removeRemovalCert,
        isLoading:
          isLoadingDelete && confirmationActionType === ELoadingRemovalCertConfirmationActionType.removeRemovalCert,
        isSuccess:
          isSuccessDelete && confirmationActionType === ELoadingRemovalCertConfirmationActionType.removeRemovalCert,
        onClick: (e: any) => {
          if (!loadingRemovalCertId) navigate(routes.t4.path);
          setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.removeRemovalCert);
          openConfirmationPortal(e);
        },
        componentType: ComponentType.Danger,
        type: EDetailPageTopBarItemType.loadingButton,
        disable: isAnyActionLoading,
      });

    if (t4TopBtnsVisibility.isShowGenerateIceCertificate)
      topBtns.push({
        buttonIconName: 'Stickies',
        title: 'Generate ICE Certificate',
        isError: isErrorRequestIceCert,
        isLoading: isLoadingRequestIceCert,
        isSuccess: isSuccessRequestIceCert,
        onClick: (e: any) => {
          setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.requestIceCert);
          openConfirmationPortal(e);
        },
        componentType: ComponentType.Success,
        type: EDetailPageTopBarItemType.loadingButton,
        disable: isAnyActionLoading,
      });

    if (t4TopBtnsVisibility.isShowWithdrawIceCertificateRequest)
      topBtns.push({
        buttonIconName: 'FileEarmarkPlus',
        title: 'Withdraw ICE Certificate',
        isError:
          confirmationActionType === ELoadingRemovalCertConfirmationActionType.withdrawIceCert && isErrorRequestIceCert,
        isLoading:
          confirmationActionType === ELoadingRemovalCertConfirmationActionType.withdrawIceCert &&
          isLoadingRequestIceCert,
        isSuccess:
          confirmationActionType === ELoadingRemovalCertConfirmationActionType.withdrawIceCert &&
          isSuccessRequestIceCert,
        onClick: (e: any) => {
          setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.withdrawIceCert);
          openConfirmationPortal(e);
        },
        componentType: ComponentType.Danger,
        type: EDetailPageTopBarItemType.loadingButton,
        disable: isAnyActionLoading,
      });

    if (t4TopBtnsVisibility.isShowSelectTwcBtn)
      topBtns.push({
        buttonIconName: 'EnvelopeCheck',
        title: 'Select TWC',
        onClick: (e: any) => {
          useFormMethods.setValue(ECreateT4FormKeys.SELECTED_TWCS, []);
          if (useFormMethods.formState.isValid) {
            openSelectTwcModal();
          }
        },
        componentType: ComponentType.Success,
        type: EDetailPageTopBarItemType.button,
        disable: isAnyActionLoading,
      });

    if (t4TopBtnsVisibility.isShowCancelBtn)
      topBtns.push({
        buttonIconName: 'FileExcel',
        title: 'Cancel',
        isError: isErrorDelete && confirmationActionType === ELoadingRemovalCertConfirmationActionType.delete,
        isLoading: isLoadingDelete && confirmationActionType === ELoadingRemovalCertConfirmationActionType.delete,
        isSuccess: isSuccessDelete && confirmationActionType === ELoadingRemovalCertConfirmationActionType.delete,
        onClick: (e: any) => {
          if (!loadingRemovalCertId) navigate(routes.t4.path);
          setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.delete);
          openConfirmationPortal(e);
        },
        componentType: ComponentType.Danger,
        type: EDetailPageTopBarItemType.loadingButton,
        disable: isAnyActionLoading,
      });

    if (t4TopBtnsVisibility.isShowPrintBtn)
      topBtns.push(
        <DownloadButton
          key="printPdf"
          exportFunction={downloadLoadingRemovalCertPdfAsync}
          defaultExportProps={{
            projectId,
            id: loadingRemovalCertId as number,
          }}
          buttonRender={(exportFile) => (
            <Button prefixIconName="Printer" type={ComponentType.Secondary} onClick={() => exportFile()}>
              Print
            </Button>
          )}
        />,
      );

    if (t4TopBtnsVisibility.isShowPrintIceCert)
      topBtns.push(
        <DownloadButton
          key="printIceCert"
          exportFunction={downloadLoadingRemovalCertIceConstructionCertPdfAsync}
          defaultExportProps={{
            projectId,
            id: loadingRemovalCertId as number,
          }}
          buttonRender={(exportFile) => (
            <Button prefixIconName="Printer" type={ComponentType.Secondary} onClick={() => exportFile()}>
              Print ICE Cert
            </Button>
          )}
        />,
      );

    if (t4TopBtnsVisibility.isShowPrintIceCertWithoutSign)
      topBtns.push(
        <DownloadButton
          key="printIceCertWithoutSign"
          exportFunction={downloadLoadingRemovalCertPdfAsync}
          defaultExportProps={{
            projectId,
            id: loadingRemovalCertId as number,
          }}
          buttonRender={(exportFile) => (
            <Button prefixIconName="Printer" type={ComponentType.Secondary} onClick={() => exportFile()}>
              Print ICE Cert W/O Sign
            </Button>
          )}
        />,
      );

    if (t4TopBtnsVisibility.isShowPrintIceCertRemovalCert)
      topBtns.push(
        <DownloadButton
          key="printIceRemovalCert"
          exportFunction={downloadLoadingRemovalCertIceRemovalCertPdfAsync}
          defaultExportProps={{
            projectId,
            id: loadingRemovalCertId as number,
          }}
          buttonRender={(exportFile) => (
            <Button prefixIconName="Printer" type={ComponentType.Secondary} onClick={() => exportFile()}>
              Print ICE Removal Cert
            </Button>
          )}
        />,
      );

    if (t4TopBtnsVisibility.isShowPrintIceCertRemovalCertWithoutSign)
      topBtns.push(
        <DownloadButton
          key="printIceRemovalCertWithoutSign"
          exportFunction={downloadLoadingRemovalCertPdfAsync}
          defaultExportProps={{
            projectId,
            id: loadingRemovalCertId as number,
          }}
          buttonRender={(exportFile) => (
            <Button prefixIconName="Printer" type={ComponentType.Secondary} onClick={() => exportFile()}>
              Print ICE Removal Cert W/O Sign
            </Button>
          )}
        />,
      );
    return topBtns;
  }, [
    isAnyActionLoading,
    JSON.stringify(loadingRemovalCertDetail),
    JSON.stringify(masterSchedule),
    isLoadingCreate,
    isLoadingUpdate,
    isLoadingCreateRemovalCert,
    isLoadingCreateRevision,
    isLoadingDelete,
    isLoadingApproveTwc,
    isLoadingApproveIce,
    isLoadingIssueSrp,
    isLoadingRequestIceCert,
    isErrorCreate,
    isErrorUpdate,
    isErrorCreateRemovalCert,
    isErrorCreateRevision,
    isErrorApproveTwc,
    isErrorApproveIce,
    isErrorIssueSrp,
    isErrorRequestIceCert,
    isSuccessCreate,
    isSuccessUpdate,
    isSuccessCreateRemovalCert,
    isSuccessCreateRevision,
    isSuccessApproveTwc,
    isSuccessApproveIce,
    isSuccessIssueSrp,
    isSuccessRequestIceCert,
    isFinalRemovalCert,
    JSON.stringify(useFormMethods.formState.isValid),
    JSON.stringify(t4TopBtnsVisibility),
    mode,
  ]);

  const topBarRightItems = React.useMemo(() => {
    return [
      // { type: EDetailPageTopBarItemType.iconButton, buttonIconName: 'PrinterFill' as IconName, onClick: () => {} },
    ];
  }, []);

  return (
    <div>
      <ApiSuccessToast
        successes={[
          { isSuccess: isSuccessCreate, message: 'Create success.', reset: resetCreate },

          { isSuccess: isSuccessUpdate, message: 'Update success.', reset: resetUpdate },
          {
            isSuccess: isSuccessDelete,
            message: 'Cancel success.',
            reset: resetDelete,
          },
          {
            isSuccess: isSuccessCreateRevision,
            message: 'Create revision success.',
            reset: resetCreateRevision,
          },
          {
            isSuccess: isSuccessCreateRemovalCert,
            message: 'Removal cert success.',
            reset: resetCreateRemovalCert,
          },
          {
            isSuccess: isSuccessRequestIceCert,
            message: 'Request ICE cert success.',
            reset: resetRequestIceCert,
          },
          {
            isSuccess: isSuccessIssueSrp,
            message: 'Issue SRP success.',
            reset: resetIssueSrp,
          },
          {
            isSuccess: isSuccessApproveTwc,
            message: 'Approve TWC success.',
            reset: resetApproveTwc,
          },
          {
            isSuccess: isSuccessApproveIce,
            message: 'Approve ICE success.',
            reset: resetApproveIce,
          },
        ]}
        callbackAfterToasted={() => {
          queryClient.invalidateQueries([
            projectId,
            loadingRemovalCertId,
            FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_DETAIL,
          ]);

          if (isSuccessCreate) {
            queryClient.invalidateQueries([projectId, masterScheduleId, FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_BY_T1]);
            useFormMethods.reset({}, { keepDirty: false });
            setTimeout(() => {
              navigate(routes.t4.view.path, { id: dataCreate?.item?.id?.toString() });
            }, 0);
            return;
          }
          if (isSuccessDelete) {
            setTimeout(() => {
              navigate(routes.t4.path);
            }, 0);
          }
          if (isSuccessCreateRevision) {
            queryClient.invalidateQueries([projectId, masterScheduleId, FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_BY_T1]);
            setTimeout(() => {
              navigate(routes.t4.view.path, { id: dataCreateRevision?.item?.id?.toString() });
            }, 0);
            return;
          }
          if (isSuccessCreateRemovalCert) {
            queryClient.invalidateQueries([projectId, masterScheduleId, FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_BY_T1]);
            setTimeout(() => {
              navigate(routes.t4.view.path, { id: dataCreateRemovalCert?.item?.id?.toString() });
            }, 0);
          }
          if (isSuccessIssueSrp) {
            closeSelectTwcModal();
          }
          useFormMethods.reset(useFormMethods.getValues());
        }}
      />
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          { error: errorCreate, genericError: 'Create Failed', reset: resetCreate },
          { error: errorUpdate, genericError: 'Update Failed', reset: resetUpdate },
          { error: errorDelete, genericError: 'Cancel Failed', reset: resetDelete },
          { error: errorCreateRevision, genericError: 'Create Revision Failed', reset: resetCreateRevision },
          { error: errorCreateRemovalCert, genericError: 'Create Removal Cert Failed', reset: resetCreateRemovalCert },
          { error: errorApproveTwc, genericError: 'TWC Approve Failed', reset: resetApproveTwc },
          { error: errorApproveIce, genericError: 'ICE Approve Failed', reset: resetApproveIce },
          { error: errorIssueSrp, genericError: 'SRP Issue Failed', reset: resetIssueSrp },
          { error: errorRequestIceCert, genericError: 'Request ICE Cert Failed', reset: resetRequestIceCert },
        ]}
      />
      <DetailPageTopBar leftItems={topBarLeftItems} rightItems={topBarRightItems} isReadOnly={isReadOnly} />
      <Grid container spacing={2} className="gcl-my-sm">
        <PermitToLoadRemoveDetailTabForm
          formFieldMethods={formFieldMethods}
          isReadOnly={isReadOnly}
          mode={mode}
          loadingRemovalCertDetail={loadingRemovalCertDetail}
          loadingRemovalCertId={loadingRemovalCertId}
          designModifications={designModifications}
          masterSchedule={masterSchedule}
        />

        {loadingRemovalCertDetail?.projectRemovalCerts && (
          <PermitToLoadRemoveDetailRelatedRemovalCert
            projectRemovalCerts={loadingRemovalCertDetail?.projectRemovalCerts}
          />
        )}
      </Grid>

      <Grid container spacing={2} className="gcl-gems-permit-to-load-remove-detail-endorsement-grid">
        <Grid item sm={12} md={6}>
          <PermitToLoadRemoveDetailPermit
            formFieldMethods={formFieldMethods}
            isReadOnly={isReadOnly}
            type={EPermitToLoadRemoveDetailPermitType.load}
            mutationApproveTwc={mutationApproveTwc}
            mutationIssueSrp={mutationIssueSrp}
            onClickApproveTwc={(e: React.MouseEvent) => {
              setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.twcApprove);
              openConfirmationPortal(e);
            }}
            onClickIssueSrp={onClickIssueSrp}
            permitDetail={
              loadingRemovalCertDetail?.isRemovalCert
                ? loadingRemovalCertDetail.projectLoadCert
                : loadingRemovalCertDetail
            }
            isRemovalCert={loadingRemovalCertDetail?.isRemovalCert ?? false}
            mode={mode}
            t4TopBtnsVisibility={t4TopBtnsVisibility}
            isEnableTwcComment={t4PermitsVisibility.isEnableLoadingCertTwcComment}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <PermitToLoadRemoveDetailPermit
            formFieldMethods={formFieldMethods}
            isReadOnly={isReadOnly}
            type={EPermitToLoadRemoveDetailPermitType.remove}
            mutationApproveTwc={mutationApproveTwc}
            mutationIssueSrp={mutationIssueSrp}
            onClickApproveTwc={(e: React.MouseEvent) => {
              setConfirmationActionType(ELoadingRemovalCertConfirmationActionType.twcApprove);
              openConfirmationPortal(e);
            }}
            onClickIssueSrp={onClickIssueSrp}
            permitDetail={loadingRemovalCertDetail?.isRemovalCert ? loadingRemovalCertDetail : undefined}
            isRemovalCert={loadingRemovalCertDetail?.isRemovalCert ?? false}
            mode={mode}
            t4TopBtnsVisibility={t4TopBtnsVisibility}
            isEnableTwcComment={t4PermitsVisibility.isEnableRemovalCertTwcComment}
          />
        </Grid>
        {loadingRemovalCertDetail?.iceSignatureRequired && (
          <Grid item sm={12} md={6}>
            <PermitToLoadRemovalDetailPermitIce
              type={EPermitToLoadRemoveDetailPermitType.load}
              formFieldMethods={formFieldMethods}
              mode={mode}
              loadingRemovalCertDetail={
                loadingRemovalCertDetail?.isRemovalCert
                  ? loadingRemovalCertDetail.projectLoadCert
                  : loadingRemovalCertDetail
              }
              isEnableIceComment={t4PermitsVisibility.isEnableLoadingCertIceComment}
            />
          </Grid>
        )}
        {t4PermitsVisibility.isShowPermitToRemoveIce && (
          <Grid item sm={12} md={6}>
            <PermitToLoadRemovalDetailPermitIce
              type={EPermitToLoadRemoveDetailPermitType.remove}
              formFieldMethods={formFieldMethods}
              mode={mode}
              loadingRemovalCertDetail={loadingRemovalCertDetail?.isRemovalCert ? loadingRemovalCertDetail : undefined}
              isEnableIceComment={t4PermitsVisibility.isEnableRemovalCertIceComment}
            />
          </Grid>
        )}
      </Grid>
      {renderConfirmationPortal(
        isLoadingCreate ||
          isLoadingUpdate ||
          isLoadingCreateRemovalCert ||
          isLoadingCreateRevision ||
          isLoadingDelete ||
          isLoadingApproveTwc ||
          isLoadingApproveIce ||
          isLoadingIssueSrp ||
          isLoadingRequestIceCert,
        isErrorCreate ||
          isErrorUpdate ||
          isErrorCreateRemovalCert ||
          isErrorCreateRevision ||
          isErrorApproveTwc ||
          isErrorApproveIce ||
          isErrorIssueSrp ||
          isErrorRequestIceCert,
        isSuccessCreate ||
          isSuccessUpdate ||
          isSuccessCreateRemovalCert ||
          isSuccessCreateRevision ||
          isSuccessApproveTwc ||
          isSuccessApproveIce ||
          isSuccessIssueSrp ||
          isSuccessRequestIceCert,
      )}
      {renderSelectTwcModal(
        mutationCreate.isLoading || mutationIssueSrp.isLoading,
        mutationCreate.isError || mutationIssueSrp.isError,
        mutationCreate.isSuccess || mutationIssueSrp.isSuccess,
      )}
    </div>
  );
};
